import {ChargesQueryParams, ChargesClient} from '../../../infrastracture/api/ChargesClient'

export function isSystemEstimateMirrorCharge(charge: any): boolean {
  return (
    String(charge?.name ?? '')
      .trim()
      .toLowerCase() === 'estimate'
  )
}

function chargeBelongsToQuoteLegacy(charge: any, quote: any): boolean {
  if (!charge?.processedOn || !quote?.processedOn) return false
  const cTime = new Date(charge.processedOn).getTime()
  const qTime = new Date(quote.processedOn).getTime()
  if (Number.isNaN(cTime) || Number.isNaN(qTime)) return false
  if (Math.abs(cTime - qTime) > 3000) return false
  const cBy = charge.processedById ?? charge.processedBy?.id
  const qBy = quote.processedById ?? quote.processedBy?.id
  if (!cBy || !qBy || String(cBy) !== String(qBy)) return false
  return true
}

function unallocatedParentChargeBelongsToQuote(charge: any, quote: any): boolean {
  if (!charge?.processedOn || !quote?.processedOn) return false
  const cTime = new Date(charge.processedOn).getTime()
  const qTime = new Date(quote.processedOn).getTime()
  if (Number.isNaN(cTime) || Number.isNaN(qTime)) return false

  if (Math.abs(cTime - qTime) > 15000) return false
  const cBy = charge.processedById ?? charge.processedBy?.id
  const qBy = quote.processedById ?? quote.processedBy?.id
  if (cBy != null && qBy != null && String(cBy) !== String(qBy)) return false
  return true
}

export function quoteLineTotalForQuote(quote: any, isContractorUser: boolean): number {
  try {
    const props = JSON.parse(quote.chargeQuoteProperties || '[]')
    return props.reduce((qSum: number, item: any) => {
      return qSum + (isContractorUser ? item.cost || 0 : item.sell || 0)
    }, 0)
  } catch {
    return 0
  }
}

export function quoteLineCostSell(quote: any): {cost: number; sell: number} {
  try {
    const props = JSON.parse(quote.chargeQuoteProperties || '[]')
    if (!Array.isArray(props)) return {cost: 0, sell: 0}
    return props.reduce(
      (acc: {cost: number; sell: number}, item: any) => ({
        cost: acc.cost + (Number(item.cost) || 0),
        sell: acc.sell + (Number(item.sell) || 0),
      }),
      {cost: 0, sell: 0}
    )
  } catch {
    return {cost: 0, sell: 0}
  }
}

export function sumChargeValues(charges: any[]): number {
  return charges.reduce((s, c) => s + (c.value || 0), 0)
}

/** Charge type names excluded from Cost (Priority by name; Admin / Percentage typically match tenant fee types). */
const COST_BUDGET_EXCLUDED_CHARGE_TYPE_NAMES = new Set([
  'priority fee',
  'admin fee',
  'percentage fee',
])

/** Matches RecalculateWorkOrderCostsCommand fee names (PO vs non-PO). */
const PROJECT_MARKUP_FEE_CHARGE_NAMES = new Set(['percentage fee', 'manufacturing fee'])

/** Tenant-calculated fees (may live on child WO); Quote Summary shows these in the Charges band, not under Work order charges. */
const SYSTEM_TENANT_FEE_CHARGE_NAMES = new Set([
  'priority fee',
  'admin fee',
  'percentage fee',
  'manufacturing fee',
])

/** Normalized fee label from charge row — prefers `name`, then charge type display name. */
export function chargeNormalizedFeeName(charge: any): string {
  return String(charge?.name ?? charge?.chargeType?.name ?? '')
    .trim()
    .toLowerCase()
}

export function isSystemTenantFeeCharge(charge: any): boolean {
  return SYSTEM_TENANT_FEE_CHARGE_NAMES.has(chargeNormalizedFeeName(charge))
}

export function isProjectMarkupFeeCharge(charge: any): boolean {
  return PROJECT_MARKUP_FEE_CHARGE_NAMES.has(chargeNormalizedFeeName(charge))
}

export function isChargeExcludedFromCostBudget(charge: any): boolean {
  return COST_BUDGET_EXCLUDED_CHARGE_TYPE_NAMES.has(chargeNormalizedFeeName(charge))
}

export function sumChargeValuesForCostBudget(charges: any[]): number {
  return charges
    .filter((c) => (c?.name || '').toLowerCase().trim() === 'estimate')
    .reduce((s, c) => s + (Number(c?.costValue ?? c?.value) || 0), 0)
}

/** Pending = unprocessed, unlinked, non-mirror. If `parentWorkOrderId` is set, only charges on that WO (aligns submitted pool with approved linking). */
export function filterPendingCharges(chargesList: any[], parentWorkOrderId?: string): any[] {
  const restrictParent = parentWorkOrderId != null && String(parentWorkOrderId).trim() !== ''
  return chargesList.filter((c: any) => {
    if (c.processedOn != null || c.quoteId || isSystemEstimateMirrorCharge(c)) return false
    if (!restrictParent) return true
    return c.workOrderId != null && String(c.workOrderId) === String(parentWorkOrderId)
  })
}

export function getChargesForQuote(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string
): any[] {
  return chargesList
    .filter((c: any) => {
      const qid = c.quoteId
      const chargeWoId = c.workOrderId
      if (qid != null && String(qid) === String(quote.id)) return true
      if (!c.processedOn) return false
      const onProjectParent = chargeWoId != null && String(chargeWoId) === String(parentWorkOrderId)

      const quoteIdUnset = qid == null || (typeof qid === 'string' && qid.trim() === '')
      if (onProjectParent && quoteIdUnset) {
        return unallocatedParentChargeBelongsToQuote(c, quote)
      }
      if (qid != null && String(qid).trim() !== '') return false
      return chargeBelongsToQuoteLegacy(c, quote)
    })
    .filter((c: any) => !isSystemEstimateMirrorCharge(c))
}

/** Same as getChargesForQuote but only charges recorded on the parent WO (excludes child-WO charges matched via quoteId or legacy heuristics). */
export function getParentWorkOrderChargesForQuote(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string
): any[] {
  return getChargesForQuote(quote, chargesList, parentWorkOrderId).filter(
    (c: any) => c.workOrderId != null && String(c.workOrderId) === String(parentWorkOrderId)
  )
}

/** Charges matched to the quote that sit on a child WO (non–system-fee lines only; fees go in the Charges band). */
export function getChildWorkOrderChargesForQuote(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string
): any[] {
  return getChargesForQuote(quote, chargesList, parentWorkOrderId).filter(
    (c: any) =>
      c.workOrderId != null &&
      String(c.workOrderId) !== String(parentWorkOrderId) &&
      !isSystemTenantFeeCharge(c)
  )
}

function mergeChargesDedupeById(lists: any[][]): any[] {
  const map = new Map<string, any>()
  for (const list of lists) {
    for (const c of list) {
      if (c?.id != null) {
        map.set(String(c.id), c)
      } else {
        const key = `anon-${c?.workOrderId}-${String(c?.name)}-${c?.value}`
        if (!map.has(key)) map.set(key, c)
      }
    }
  }
  return Array.from(map.values())
}

/** Submitted: charges already linked to this quote on a child WO (quoteId set), excluding system fees and estimate mirror. */
export function getQuoteIdLinkedChildWorkOrderChargesForQuote(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string
): any[] {
  return chargesList.filter(
    (c: any) =>
      c.quoteId != null &&
      String(c.quoteId).trim() !== '' &&
      String(c.quoteId) === String(quote.id) &&
      c.workOrderId != null &&
      String(c.workOrderId) !== String(parentWorkOrderId) &&
      !isSystemEstimateMirrorCharge(c) &&
      !isSystemTenantFeeCharge(c)
  )
}

/** Unprocessed, unlinked charges on the quote’s child WO (not on project parent). Shown under submitted quotes in Quote Summary. */
export function getPendingChildWorkOrderChargesForQuote(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string
): any[] {
  const childWoId = quote?.workOrderId
  if (
    childWoId == null ||
    String(childWoId).trim() === '' ||
    String(childWoId) === String(parentWorkOrderId)
  ) {
    return []
  }
  return chargesList.filter((c: any) => {
    if (c.processedOn != null) return false
    const qid = c.quoteId
    if (qid != null && String(qid).trim() !== '') return false
    if (isSystemEstimateMirrorCharge(c) || isSystemTenantFeeCharge(c)) return false
    return c.workOrderId != null && String(c.workOrderId) === String(childWoId)
  })
}

/**
 * Child WO charges rendered under a quote in Quote Summary / PDF: approved uses full quote linkage;
 * submitted merges quoteId-linked child lines with pending (unprocessed) child WO charges.
 */
export function getChildWorkOrderChargesForQuoteDisplay(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string
): any[] {
  const statusLower = (quote?.quoteStatus?.name || '').toLowerCase()
  if (statusLower === 'approved') {
    return getChildWorkOrderChargesForQuote(quote, chargesList, parentWorkOrderId)
  }
  if (statusLower === 'submitted') {
    return mergeChargesDedupeById([
      getQuoteIdLinkedChildWorkOrderChargesForQuote(quote, chargesList, parentWorkOrderId),
      getPendingChildWorkOrderChargesForQuote(quote, chargesList, parentWorkOrderId),
    ])
  }
  return getChildWorkOrderChargesForQuote(quote, chargesList, parentWorkOrderId)
}

export type ProjectMarkupFeeRow = {charge: any; workOrderLabel: string}

/** Deduped parent WO charges linked to approved quotes (Quote Summary / PDF top Charges band). */
export function collectParentWorkOrderChargeRows(
  approvedQuotes: any[],
  chargesList: any[],
  parentWorkOrderId: string
): ProjectMarkupFeeRow[] {
  const byId = new Map<string, ProjectMarkupFeeRow>()
  for (const quote of approvedQuotes) {
    const linked = getParentWorkOrderChargesForQuote(quote, chargesList, parentWorkOrderId)
    for (const c of linked) {
      const idKey = c?.id != null ? String(c.id) : `${quote.id}-${String(c?.name)}-${c?.value}`
      if (byId.has(idKey)) continue
      const wo = quote.workOrder
      const workOrderLabel =
        wo?.workOrderNumber || (wo?.workOrderNumberId != null ? `WO-${wo.workOrderNumberId}` : '—')
      byId.set(idKey, {charge: c, workOrderLabel})
    }
  }
  return Array.from(byId.values())
}

/** Deduped child WO system/tenant fee charges linked to approved quotes (Charges band). */
export function collectChildSystemFeeChargeRows(
  approvedQuotes: any[],
  chargesList: any[],
  parentWorkOrderId: string
): ProjectMarkupFeeRow[] {
  const byId = new Map<string, ProjectMarkupFeeRow>()
  for (const quote of approvedQuotes) {
    const linked = getChargesForQuote(quote, chargesList, parentWorkOrderId).filter(
      (c: any) =>
        c.workOrderId != null &&
        String(c.workOrderId) !== String(parentWorkOrderId) &&
        isSystemTenantFeeCharge(c)
    )
    for (const c of linked) {
      const idKey = c?.id != null ? String(c.id) : `${quote.id}-${String(c?.name)}-${c?.value}`
      if (byId.has(idKey)) continue
      const wo = quote.workOrder
      const workOrderLabel =
        wo?.workOrderNumber || (wo?.workOrderNumberId != null ? `WO-${wo.workOrderNumberId}` : '—')
      byId.set(idKey, {charge: c, workOrderLabel})
    }
  }
  return Array.from(byId.values())
}

/** Deduped percentage/manufacturing fee charges linked to approved quotes (for project-level summary band). */
export function collectProjectMarkupFeeRows(
  approvedQuotes: any[],
  chargesList: any[],
  parentWorkOrderId: string
): ProjectMarkupFeeRow[] {
  const byId = new Map<string, ProjectMarkupFeeRow>()
  for (const quote of approvedQuotes) {
    const linked = getChargesForQuote(quote, chargesList, parentWorkOrderId)
    for (const c of linked) {
      if (!isProjectMarkupFeeCharge(c)) continue
      const idKey = c?.id != null ? String(c.id) : `${quote.id}-${String(c?.name)}-${c?.value}`
      if (byId.has(idKey)) continue
      const wo = quote.workOrder
      const workOrderLabel =
        wo?.workOrderNumber || (wo?.workOrderNumberId != null ? `WO-${wo.workOrderNumberId}` : '—')
      byId.set(idKey, {charge: c, workOrderLabel})
    }
  }
  return Array.from(byId.values())
}

/** Linked WO charges for table display under a quote (child WO only; parent WO charges show in the project Charges band). */
export function woChargesForQuoteDisplay(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string
): any[] {
  return getChildWorkOrderChargesForQuoteDisplay(quote, chargesList, parentWorkOrderId)
}

export type QuoteCostSellBreakdownWithCharges = {
  lineCost: number
  lineSell: number
  chargePortion: number
  chargePortionCost: number
  pendingPoolSell: number
  pendingPoolCost: number
  linkedCharges: any[]
  includesSharedPending: boolean
}

export function quoteCostSellBreakdownWithCharges(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string,
  quoteStatusName: string | undefined
): QuoteCostSellBreakdownWithCharges {
  const {cost: lineCost, sell: lineSell} = quoteLineCostSell(quote)
  const statusLower = (quoteStatusName || quote?.quoteStatus?.name || '').toLowerCase()

  const linkedCharges =
    statusLower === 'approved'
      ? getChargesForQuote(quote, chargesList, parentWorkOrderId)
      : statusLower === 'submitted'
        ? getChildWorkOrderChargesForQuoteDisplay(quote, chargesList, parentWorkOrderId)
        : []

  const linkedSumSell = sumChargeValues(linkedCharges)
  const linkedSumCost = sumChargeValuesForCostBudget(linkedCharges)

  const pending = filterPendingCharges(chargesList, parentWorkOrderId)
  const pendingPoolSell = sumChargeValues(pending)
  const pendingPoolCost = sumChargeValuesForCostBudget(pending)
  const includesSharedPending = statusLower === 'submitted' && pending.length > 0

  const chargePortion = linkedSumSell + (includesSharedPending ? pendingPoolSell : 0)
  const chargePortionCost = linkedSumCost + (includesSharedPending ? pendingPoolCost : 0)

  return {
    lineCost,
    lineSell,
    chargePortion,
    chargePortionCost,
    pendingPoolSell: includesSharedPending ? pendingPoolSell : 0,
    pendingPoolCost: includesSharedPending ? pendingPoolCost : 0,
    linkedCharges,
    includesSharedPending,
  }
}

export function quoteCostSellWithCharges(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string,
  quoteStatusName: string | undefined
): {cost: number; sell: number} {
  const b = quoteCostSellBreakdownWithCharges(
    quote,
    chargesList,
    parentWorkOrderId,
    quoteStatusName
  )
  return {
    cost: b.lineCost + b.chargePortionCost,
    sell: b.lineSell + b.chargePortion,
  }
}

export function quoteDisplayTotalWithCharges(
  quote: any,
  chargesList: any[],
  parentWorkOrderId: string,
  quoteStatusName: string | undefined,
  isContractorUser: boolean
): number {
  const {cost, sell} = quoteCostSellWithCharges(
    quote,
    chargesList,
    parentWorkOrderId,
    quoteStatusName
  )
  return isContractorUser ? cost : sell
}

export async function fetchMergedChargesForWorkOrders(workOrderIds: string[]): Promise<any[]> {
  const unique = Array.from(new Set(workOrderIds.filter(Boolean)))
  if (unique.length === 0) return []
  const responses = await Promise.all(
    unique.map((id) =>
      ChargesClient.List(
        new ChargesQueryParams().WithWorkOrderId(id).WithIsActive(true).Paginate(0, 1000)
      )
    )
  )
  const byId = new Map<string, any>()
  for (const res of responses) {
    if (!res.successful) continue
    for (const row of res.data?.data || []) {
      if (row?.id) byId.set(row.id, row)
    }
  }
  return Array.from(byId.values())
}
